import React, { memo } from 'react';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import type { UIEvent } from 'react';
import AdminSurface from '../../../components/admin/AdminSurface';
import type { AccountHolderOwner, AccountHoldersAudienceFilter } from '../../../types/features/accountHolders';
import { adminColors, adminTypography } from '../../../styles/adminDesignTokens';
import {
  adminAlertSx,
  adminAlertToneSx,
  adminCombineSx,
  adminEmptyStateSx,
  adminIconActionButtonSx,
  adminTableSx,
  adminTextLinkSx,
} from '../../../styles/adminComponentStyles';
import {
  filterAndSortAccountHolderOwners,
  formatDate,
  formatOwnerStateCode,
  formatPhoneNumber,
  formatStoresCount,
  type AccountHoldersSortDirection,
  type AccountHoldersSortKey,
} from '../mappers/accountHoldersFormatters';

type AccountHoldersTableProps = {
  owners: AccountHolderOwner[];
  search: string;
  audienceFilter: AccountHoldersAudienceFilter;
  loading: boolean;
  loadingMore: boolean;
  error: string;
  deleteError: string;
  deletingOwnerId: string | null;
  onTableScroll: (event: UIEvent<HTMLDivElement>) => void;
  onOwnerClick: (owner: AccountHolderOwner) => void;
  onDeleteLead: (owner: AccountHolderOwner) => void;
};

const columns: Array<{
  align?: 'center' | 'left' | 'right';
  key: AccountHoldersSortKey;
  label: string;
  width: string;
}> = [
  { key: 'registration_date', label: 'Date', width: '12%' },
  { key: 'name', label: 'Name', width: '23%' },
  { key: 'state', label: 'State', width: '8%' },
  { key: 'email_address', label: 'Email', width: '25%' },
  { key: 'phone_number', label: 'Phone', width: '16%' },
  { key: 'phone_verified', label: 'Verified', align: 'center', width: '8%' },
  { key: 'no_of_stores', label: 'Stores', align: 'right', width: '8%' },
];

const leadDeleteColumnWidths: Record<AccountHoldersSortKey, string> = {
  registration_date: '11%',
  name: '22%',
  state: '7%',
  email_address: '24%',
  phone_number: '15%',
  phone_verified: '7%',
  no_of_stores: '7%',
};

const stopEmailClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
  event.stopPropagation();
};

const stopEmailKeyDown = (event: React.KeyboardEvent<HTMLAnchorElement>) => {
  event.stopPropagation();
};

const AccountHoldersTable: React.FC<AccountHoldersTableProps> = ({
  owners,
  search,
  audienceFilter,
  loading,
  loadingMore,
  error,
  deleteError,
  deletingOwnerId,
  onTableScroll,
  onOwnerClick,
  onDeleteLead,
}) => {
  const [sort, setSort] = React.useState<{ key: AccountHoldersSortKey; direction: AccountHoldersSortDirection }>({
    key: 'registration_date',
    direction: 'desc',
  });

  const showLeadDeleteActions = audienceFilter === 'leads_without_stores';
  const tableColumnCount = columns.length + (showLeadDeleteActions ? 1 : 0);

  const visibleOwners = React.useMemo(
    () => filterAndSortAccountHolderOwners(owners, search, audienceFilter, sort.key, sort.direction),
    [audienceFilter, owners, search, sort.direction, sort.key],
  );

  const handleSort = React.useCallback((key: AccountHoldersSortKey) => {
    setSort((currentSort) => ({
      key,
      direction: currentSort.key === key && currentSort.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const handleDeleteClick = React.useCallback((
    event: React.MouseEvent<HTMLButtonElement>,
    owner: AccountHolderOwner,
  ) => {
    event.stopPropagation();
    onDeleteLead(owner);
  }, [onDeleteLead]);

  const stopDeleteKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLButtonElement>) => {
    event.stopPropagation();
  }, []);

  return (
    <AdminSurface sx={{ border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgb(34 43 69 / 8%)', overflow: 'hidden' }}>
      {deleteError ? (
        <Box sx={{ p: 2 }}>
          <Alert severity="error" sx={adminCombineSx(adminAlertSx, adminAlertToneSx.danger)}>
            {deleteError}
          </Alert>
        </Box>
      ) : null}
      <TableContainer
        onScroll={onTableScroll}
        sx={{
          height: 'calc(100vh - 132px)',
          maxHeight: 'calc(100vh - 132px)',
          minHeight: 420,
          overflow: 'auto',
        }}
      >
        <Table
          size="small"
          aria-label="account holders table"
          sx={adminCombineSx(adminTableSx, {
            tableLayout: 'fixed',
            '& .MuiTableCell-root': {
              padding: '8px 12px !important',
            },
            '& .MuiTableHead-root .MuiTableCell-head': {
              textTransform: 'uppercase',
            },
            '& .MuiTableHead-root': {
              position: 'sticky',
              top: 0,
              zIndex: 1,
            },
            '& .MuiTableBody-root .MuiTableRow-root': {
              height: 44,
            },
            '& .MuiTableBody-root .MuiTableRow-root.AdminClickableRow': {
              cursor: 'pointer',
            },
          })}
        >
          <TableHead>
            <TableRow>
              {columns.map((column) => {
                const active = sort.key === column.key;
                const columnWidth = showLeadDeleteActions ? leadDeleteColumnWidths[column.key] : column.width;

                return (
                  <TableCell
                    key={column.key}
                    align={column.align}
                    sortDirection={active ? sort.direction : false}
                    sx={{ width: columnWidth }}
                  >
                    <TableSortLabel
                      active={active}
                      direction={active ? sort.direction : 'asc'}
                      hideSortIcon={false}
                      onClick={() => handleSort(column.key)}
                      sx={{
                        justifyContent:
                          column.align === 'right'
                            ? 'flex-end'
                            : column.align === 'center'
                              ? 'center'
                              : 'flex-start',
                        width: '100%',
                      }}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                );
              })}
              {showLeadDeleteActions ? (
                <TableCell align="center" sx={{ width: '7%' }}>
                  Action
                </TableCell>
              ) : null}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={tableColumnCount} sx={{ border: 0 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} />
                  </Box>
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={tableColumnCount} sx={{ border: 0, p: 1.5 }}>
                  <Alert severity="error" sx={adminCombineSx(adminAlertSx, adminAlertToneSx.danger)}>
                    {error}
                  </Alert>
                </TableCell>
              </TableRow>
            ) : visibleOwners.length === 0 ? (
              <TableRow>
                <TableCell colSpan={tableColumnCount} sx={{ border: 0, p: 1.5 }}>
                  <Box sx={adminCombineSx(adminEmptyStateSx, { border: 0, minHeight: 80 })}>
                    <Typography className="AdminEmptyState-title">No users found</Typography>
                    <Typography className="AdminEmptyState-copy">Try another search or filter.</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {visibleOwners.map((owner) => {
                  const emailAddress = owner.email_address.trim();
                  const isDeleting = deletingOwnerId === owner.user_id;

                  return (
                    <TableRow
                      key={owner.user_id}
                      hover
                      className="AdminClickableRow"
                      onClick={() => onOwnerClick(owner)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onOwnerClick(owner);
                        }
                      }}
                    >
                      <TableCell sx={{ width: showLeadDeleteActions ? '11%' : '12%' }}>
                        {formatDate(owner.registration_date)}
                      </TableCell>
                      <TableCell sx={{ width: showLeadDeleteActions ? '22%' : '23%' }}>
                        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, minWidth: 0 }}>
                          <Typography
                            noWrap
                            sx={{ ...adminTypography.bodyStrong, color: adminColors.text, minWidth: 0 }}
                          >
                            {owner.name || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ width: showLeadDeleteActions ? '7%' : '8%' }}>
                        <Typography noWrap sx={{ ...adminTypography.body, color: adminColors.text }}>
                          {formatOwnerStateCode(owner)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: showLeadDeleteActions ? '24%' : '25%' }}>
                        {emailAddress ? (
                          <Box
                            component="a"
                            href={`mailto:${emailAddress}`}
                            onClick={stopEmailClick}
                            onKeyDown={stopEmailKeyDown}
                            sx={adminCombineSx(adminTextLinkSx, {
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            })}
                          >
                            {emailAddress}
                          </Box>
                        ) : (
                          <Typography noWrap sx={{ ...adminTypography.body, color: adminColors.text }}>
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ width: showLeadDeleteActions ? '15%' : '16%' }}>
                        {formatPhoneNumber(owner.phone_number)}
                      </TableCell>
                      <TableCell align="center" sx={{ width: showLeadDeleteActions ? '7%' : '8%' }}>
                        {owner.phone_verified ? (
                          <CheckCircleRoundedIcon
                            aria-label="Phone verified"
                            sx={{ color: adminColors.success, display: 'block', fontSize: 18, mx: 'auto' }}
                          />
                        ) : (
                          <RadioButtonUncheckedRoundedIcon
                            aria-label="Phone not verified"
                            sx={{ color: adminColors.disabledText, display: 'block', fontSize: 18, mx: 'auto' }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ width: showLeadDeleteActions ? '7%' : '8%' }}>
                        {formatStoresCount(owner.no_of_stores)}
                      </TableCell>
                      {showLeadDeleteActions ? (
                        <TableCell align="center" sx={{ width: '7%' }}>
                          <Tooltip title="Delete lead">
                            <span>
                              <IconButton
                                aria-label={`Delete ${owner.name || 'lead'}`}
                                disabled={isDeleting}
                                onClick={(event) => handleDeleteClick(event, owner)}
                                onKeyDown={stopDeleteKeyDown}
                                sx={adminCombineSx(adminIconActionButtonSx, {
                                  color: adminColors.danger,
                                  '&:hover': {
                                    backgroundColor: adminColors.dangerBackground,
                                  },
                                })}
                              >
                                {isDeleting ? (
                                  <CircularProgress color="inherit" size={16} />
                                ) : (
                                  <CloseRoundedIcon />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  );
                })}
                {loadingMore ? (
                  <TableRow>
                    <TableCell colSpan={tableColumnCount} sx={{ border: 0 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : null}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </AdminSurface>
  );
};

export default memo(AccountHoldersTable);
